import { useEffect, useState } from "react";
import { IconDownload, IconX } from "@tabler/icons-react";
import { Button } from "../../../../components/common/button";
import { Heading, Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { productDatasheetsService } from "../../../api_client";

type Props =
  | { productTemplateId: number; url?: undefined; fileName?: undefined; onClose: () => void }
  | { url: string; fileName: string; productTemplateId?: undefined; onClose: () => void };

interface State {
  url: string;
  fileName: string;
}

export function DatasheetPreviewModal(props: Props) {
  const { productTemplateId, onClose } = props;
  const directUrl = props.url;
  const directFileName = props.fileName;
  const [data, setData] = useState<State | null>(
    directUrl && directFileName ? { url: directUrl, fileName: directFileName } : null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (productTemplateId == null) return;
    let cancelled = false;
    setError(null);
    setData(null);
    productDatasheetsService
      .getTemplateDownloadUrl(productTemplateId)
      .then((res) => {
        if (cancelled) return;
        setData({ url: res.data.url, fileName: res.data.fileName });
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load datasheet preview. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [productTemplateId]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[85vh] w-full max-w-4xl flex-col gap-3 rounded-2xl bg-bgWhite p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <Heading variant="h3" tone="dark" className="truncate">
            {data?.fileName ?? "Product datasheet"}
          </Heading>
          <div className="flex items-center gap-2">
            {data?.url ? (
              <a
                href={data.url}
                download={data.fileName}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex"
              >
                <Button variant="secondary" size="sm" leftIcon={<IconDownload size={18} />}>
                  Download
                </Button>
              </a>
            ) : null}
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="inline-flex size-9 items-center justify-center rounded-full text-textBody hover:bg-bgLight"
            >
              <IconX size={20} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden rounded-xl border border-borderDefault bg-bgLight">
          {error ? (
            <div className="flex h-full items-center justify-center p-6">
              <Text variant="body" tone="body" className="!text-accentError">
                {error}
              </Text>
            </div>
          ) : data ? (
            <iframe
              src={data.url}
              title="Product datasheet"
              className="h-full w-full"
            />
          ) : (
            <div className="flex h-full items-center justify-center p-6">
              <ShimmerLoader message="Loading datasheet preview…" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
